import { ChevronDownIcon, ChevronRightIcon, CloseIcon, LockIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { assetRepository } from 'api/assetRepository';
import { vendorRepository } from 'api/vendorRepository';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { editAssetSchema, flattenZodErrors } from 'validations/Validations';
import { z } from 'zod';
export default function EditAssetForm() {
  const { state } = useLocation();
  const asset: any = state || {};
  const navigate = useNavigate();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [assetTypes, setAssetTypes] = useState<any[]>([]);
  const [subTypes, setSubTypes] = useState<string[]>([]);
  const [hoveredType, setHoveredType] = useState<any>(null);
  const [headerContext, setHeaderContext] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]);
  const [vendorProducts, setVendorProducts] = useState<any[]>([]);
  const inputBg = useColorModeValue('white', 'navy.700');
  const inputBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const menuListBg = useColorModeValue('white', 'navy.800');
  const menuHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const subMenuShadow = useColorModeValue('md', 'dark-lg');

  const [formData, setFormData] = useState({
    id: asset.assetId || '',
    assetEquipmentId: asset.assetEquipmentId || '',
    assetName: asset.name || '',
    assetType: asset.type || '',
    assetSubType: asset.subType || '',
    vendorId: asset.vendorId || '',
    status: asset.status || 'Active',
    installedDate: formatDate(asset.installedDate) || '',
  });
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    'success' | 'info' | 'warning' | 'error'

  >('success');
  const centerAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const showCenteredAlert = (
    msg: string,
    status: 'success' | 'info' | 'warning' | 'error' = 'success',
    duration = 2000
  ) => {
    if (centerAlertTimerRef.current) {
      clearTimeout(centerAlertTimerRef.current);
      centerAlertTimerRef.current = null;
    }
    setCenterAlertMessage(msg);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    centerAlertTimerRef.current = setTimeout(() => {
      setCenterAlertVisible(false);
      centerAlertTimerRef.current = null;
    }, duration);
  };

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await assetRepository.getProductTypes();
        setAssetTypes(types);


        if (asset.type) {
          const selected = types.find((t: any) => t.type === asset.type);
          setSubTypes(selected?.subType || []);
        }
      } catch (error) {
        console.error('Error fetching product types:', error);
      }

    };
    fetchTypes();

  }, [asset.type]);

  useEffect(() => {

    const fetchVendors = async () => {

      try {

        const data = await vendorRepository.getVendors();

        setVendors(data || []);

      } catch (error) {

        console.error('Failed to fetch vendors', error);

      }

    };
    fetchVendors();

  }, []);

  useEffect(() => {
    if (!formData.vendorId) return;

    handleVendorChange(formData.vendorId, { preserveCurrentName: true });
  }, [formData.vendorId]);


  useEffect(() => {

    const orgId = asset?.orgId || '';
    const locId = asset?.locationId || '';
    const assetId = asset?.assetId || formData.id || '';



    if (orgId || locId || assetId) {

      assetRepository
        .getHeaderContext(orgId, locId, assetId, formData.vendorId)
        .then((data) => {
          setHeaderContext(data);
        })
        .catch((err) => {
          console.error('Failed to fetch header context', err);
          setHeaderContext(null);
        });

    }

  }, [asset, formData.id, formData.vendorId]);



  const renderTextWithTooltip = (

    label: string,

    value?: string,

    maxLength = 20

  ) => {

    if (!value) value = '-';
    const isLong = value.length > maxLength;
    const displayValue = isLong
      ? value.slice(0, maxLength) + '...'
      : value;
    return isLong ? (
      <Tooltip label={value} hasArrow placement="top">
        <Text fontSize="sm" noOfLines={1} maxW="200px">
          {label}: {displayValue}
        </Text>
      </Tooltip>
    ) : (
      <Text fontSize="sm" noOfLines={1} maxW="200px">
        {label}: {displayValue}
      </Text>
    );
  };



  const handleTypeChange = (value: string) => {

    setFormData((prev) => ({

      ...prev,

      assetType: value,

      assetSubType: '',

    }));


    const selected = assetTypes.find((t) => t.type === value);
    setSubTypes(selected?.subType || []);
  };
  const handleChange = (

    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>

  ) => {

    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleVendorChange = async (
    vendorId: string,
    options: { preserveCurrentName?: boolean } = {}
  ) => {
    try {
      const currentAssetName = formData.assetName;
      const currentAssetType = formData.assetType;

      setFormData((prev) => ({
        ...prev,
        vendorId,
        ...(options.preserveCurrentName
          ? {}
          : { assetName: "", assetType: "" }),
      }));

      const response = await vendorRepository.getvendorProductbyVendor(vendorId);
      let products = response[0]?.products || [];

      if (
        options.preserveCurrentName &&
        currentAssetName &&
        !products.some((p: any) => p.productName === currentAssetName)
      ) {
        products = [
          {
            productId: 'current-asset',
            productName: currentAssetName,
            productType: currentAssetType,
          },
          ...products,
        ];
      }

      setVendorProducts(products);
    } catch (error) {
      console.error(error);
      setVendorProducts([]);
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      editAssetSchema.parse({
        id: formData.id,
        assetEquipmentId: formData.assetEquipmentId,
        assetName: formData.assetName,
        assetType: formData.assetType,
        assetSubType: formData.assetSubType,
        status: formData.status,
        //installedDate: formData.installedDate,
      });

      setErrors({});

      await assetRepository.updateAsset(formData.id, {
        assetEquipmentId: formData.assetEquipmentId,
        name: formData.assetName,
        type: formData.assetType,
        subType: formData.assetSubType,
        vendorId: formData.vendorId,
        status: formData.status,
        //  installedDate: formData.installedDate,
      });

      showCenteredAlert('Asset updated successfully', 'success');
      setTimeout(() => {
        navigate(`/admin/asset-management/AssetDetail`, {
          state: {
            assetId: formData.id,
            orgId: asset.orgId,
            locationId: asset.locationId,
            departmentId: asset.departmentId,
            vendorId: formData.vendorId,
          },
        });
      }, 1200);
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);

      } else if (error.response?.data) {
        const backendMessage = error.response.data;

        if (

          backendMessage.toLowerCase().includes('equipment') ||

          backendMessage.toLowerCase().includes('duplicate')

        ) {
          setErrors((prev) => ({
            ...prev,
            assetEquipmentId: backendMessage,
          }));

          showCenteredAlert(backendMessage, "error");

        } else {

          showCenteredAlert(backendMessage, 'error');

        }

      } else {

        console.error('Error updating asset:', error);

        showCenteredAlert('Failed to update asset', 'error');

      }

    } finally {

      setIsUpdating(false);

    }

  };

  useEffect(() => {
    if (headerContext?.assetInfo?.assetEquipmentId) {
      setFormData(prev => ({
        ...prev,
        assetEquipmentId: headerContext.assetInfo.assetEquipmentId,
      }));
    }
  }, [headerContext]);

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="center"
      position="relative"
      px={{ base: '20px', md: '40px' }}
      pb="60px">
      <Box
        position="absolute"
        top="90"
        left="0"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        zIndex={0}
        borderRadius="20px" />

      <Box
        w="100%"
        mt={{ md: '100px' }}
        mb="20px"
        zIndex={2}>
        <Flex justify="center">
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            gap="20px"
            w="fit-content"
          >
            {/* Organization */}
            <Stack
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg={useColorModeValue('white', 'gray.700')}
              shadow="md"
              align="start"
              h="150px"
            >
              <Text fontWeight="bold" textAlign="center" w="100%">
                Organization
              </Text>
              {renderTextWithTooltip('Name', headerContext?.orgInfo?.name)}
              {renderTextWithTooltip(
                'Contact',
                headerContext?.orgInfo?.contact
              )}
              {renderTextWithTooltip('GSTIN', headerContext?.orgInfo?.gsTin)}

            </Stack>

            {/* Location */}
            <Stack
              p={4}
              w="250px"
              borderWidth="1px"
              borderRadius="md"
              bg={useColorModeValue('white', 'gray.700')}
              shadow="md"
              align="start"
              h="150px"
            >
              <Text fontWeight="bold" textAlign="center" w="100%">
                Plant
              </Text>
              {renderTextWithTooltip(
                'Name',
                headerContext?.locationInfo?.name

              )}

              {renderTextWithTooltip(
                'City',
                headerContext?.locationInfo?.city

              )}

              {renderTextWithTooltip(

                'State',
                headerContext?.locationInfo?.state

              )}

            </Stack>

          </SimpleGrid>
        </Flex>

      </Box>

      <Card
        w={{ sm: '330px', md: '700px', lg: '850px' }}
        zIndex={1}
        borderRadius="16px"
      >
        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
          Edit Asset
        </Text>
        <IconButton
          icon={<CloseIcon />}
          aria-label="Close"
          position="absolute"
          top="10px"
          right="10px"
          size="sm"
          onClick={() =>
            navigate("/admin/asset-management/AssetDetail", {
              state: {
                assetId: formData.id,
                orgId: asset.orgId,
                locationId: asset.locationId,
                departmentId: asset.departmentId,
                vendorId: formData.vendorId,
                pageIndex: asset.pageIndex,
              },
            })

          }
        />

        <Flex direction="column" w="100%">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            <Stack gap="20px">
              {/* Vendor Name */}
              <FormControl>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  mb="8px"
                  color={textColor}
                >
                  Vendor
                </Text>

                <Select
                  name="vendorId"
                  value={formData.vendorId}
                  onChange={(e) => handleVendorChange(e.target.value)}
                  h="44px"
                  bg={useColorModeValue("white", "#1F2A44")}
                  color="white"
                  borderColor={inputBorderColor}
                >
                  <option value="" style={{ backgroundColor: "#1F2A44", color: "white" }}>
                    Select Vendor
                  </option>

                  {vendors.map((vendor) => (
                    <option
                      key={vendor.vendorId}
                      value={vendor.vendorId}
                      style={{
                        backgroundColor: "#1F2A44",
                        color: "white",
                      }}
                    >
                      {vendor.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* {Equipment ID is not editable, so we are displaying it in a disabled input field.} */}
              <FormControl isInvalid={!!errors?.assetEquipmentId}>
                <InputField
                  label="Asset Equipment ID"
                  id="assetEquipmentId"
                  value={formData.assetEquipmentId}
                  name="assetEquipmentId"
                  isDisabled={true}
                  mb="0"
                />
                <FormErrorMessage>
                  {errors?.assetEquipmentId}
                </FormErrorMessage>
              </FormControl>

              {/* Status */}

              <FormControl isInvalid={!!errors.status}>

                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  mb="8px"
                  color={textColor}
                >
                  Status
                </Text>
                <Select

                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  h="44px"
                >
                  <option value="Active">Active</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Inactive">Inactive</option>
                </Select>
                {errors.status && (

                  <FormErrorMessage>{errors.status}</FormErrorMessage>
                )}
              </FormControl>
            </Stack>

            <Stack gap="20px">
              {/* {Asset Name} */}
              <FormControl isInvalid={!!errors.assetName}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  mb="8px"
                  color={textColor}
                >
                  Asset Name
                </Text>

                <Select
                  placeholder="Select Asset Name"
                  value={formData.assetName}
                  onChange={(e) => {
                    const product = vendorProducts.find(
                      (p: any) => p.productName === e.target.value
                    );

                    setFormData((prev) => ({
                      ...prev,
                      assetName: product?.productName || "",
                      assetType: product?.productType || "",
                    }));
                  }}
                >
                  {formData.assetName &&
                    !vendorProducts.some(
                      (p: any) => p.productName === formData.assetName
                    ) && (
                      <option value={formData.assetName}>
                        {formData.assetName}
                      </option>
                    )}

                  {vendorProducts.map((product: any) => (
                    <option
                      key={product.productId}
                      value={product.productName}
                    >
                      {product.productName}
                    </option>
                  ))}
                </Select>
                {errors.assetName && (
                  <FormErrorMessage>{errors.assetName}</FormErrorMessage>
                )}
              </FormControl>

              {/* Vendor Dropdown */}
              <FormControl isInvalid={!!errors.assetType}>
                <InputField
                  label="Asset Type"
                  id="assetType"
                  value={formData.assetType || ''}
                  isReadOnly={true}
                  mb="0"
                />
                <Tooltip
                  label="This field cannot be edited"
                  placement="right"
                >

                  <Box
                    position="absolute"
                    right="12px"
                    top="36px"
                    color="gray.400"
                  >
                    <LockIcon />
                  </Box>
                </Tooltip>
                {errors.assetType && (
                  <FormErrorMessage>{errors.assetType}</FormErrorMessage>

                )}

              </FormControl>
            </Stack>
          </SimpleGrid>

          <Flex justify="center" gap="20px" mt="32px">

            <Button
              variant="darkBrand"
              w={{ base: '128px', md: '148px' }}
              onClick={handleUpdate}
              isLoading={isUpdating}
              disabled={isUpdating}
            >
              Update
            </Button>
          </Flex>
        </Flex>
      </Card>


      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}